import React, { useCallback, useEffect, useState } from 'react';
import { InputProps } from '@material-ui/core/Input';
import debounce from 'debounce';
import { useDispatch, useSelector } from 'react-redux';
import { FormHelperText } from '@material-ui/core';
import {
  applyPromotionCodeToActiveSubscriptionRequest,
  clearValidationPromotionCodeWIthProduct,
  validatePromotionCodeWithProductRequest,
} from '../../store/modules/promotion-codes/actions';
import { selectUpdatePlanPromotionCode, selectVerifiedCode } from '../../store/modules/promotion-codes/selectors';
import StatusIcon from '../password-error/StatusIcon';
import useStyles from './styles';
import InlineDots from './InlineDots';
import Input from '../form-input/Input';
import { User } from '../../types/items';

type Props = Omit<InputProps, 'classes'> & {
  autoCheck?: boolean;
  className?: string;
  user: User;
};

const PromotionCodeInput = ({
  autoCheck = true,
  user,
}: Props) => {
  const dispatch = useDispatch();
  const [value, setValue] = useState('');
  const { data: promoCode, loading: verifyLoading, error } = useSelector(selectVerifiedCode);
  const classes = useStyles();
  const { data: promotionCode } = useSelector(selectVerifiedCode);
  const { loading: applyPromoLoading } = useSelector(selectUpdatePlanPromotionCode);

  useEffect(() => {
    // save promo to current sub
    if (promotionCode && user?.id) {
      dispatch(applyPromotionCodeToActiveSubscriptionRequest({ userId: user.id, promotionCode: promotionCode.code }));
    }
  }, [promotionCode, user, dispatch]);

  useEffect(() => {
    dispatch(clearValidationPromotionCodeWIthProduct());
  }, [dispatch, user]);

  const checkPromotionCode = useCallback((code: string) => {
    if (autoCheck) {
      if (code && code.length > 7) {
        dispatch(validatePromotionCodeWithProductRequest(code));
      } else {
        dispatch(clearValidationPromotionCodeWIthProduct());
      }
    }
  }, [dispatch, autoCheck]);

  const checkPromotionCodeHandler = useCallback(debounce((search: string) => { checkPromotionCode(search); }, 1500),
    [checkPromotionCode]);

  useEffect(() => {
    checkPromotionCodeHandler(value);
  }, [value, checkPromotionCodeHandler]);

  const onChangeHandler = useCallback((val: string) => {
    setValue(val);
  }, [setValue]);

  return (
    <div>
      <Input
        className={`${classes.promoField} ${classes.uppercaseField}`}
        value={value}
        onChange={onChangeHandler}
        label="Promotion code"
        disabled={applyPromoLoading || verifyLoading}
      />

      {verifyLoading && <FormHelperText>Verifying<InlineDots/></FormHelperText>}
      {error && (
        <FormHelperText className={`${classes.helperText} ${classes.helperTextError}`}>
          <StatusIcon state={false} className={classes.icon} />
          Sorry, this Promo Code is not valid
        </FormHelperText>
      )}
      {promoCode && !verifyLoading && (
        <FormHelperText className={`${classes.helperText} ${classes.helperTextSuccess}`}>
          <StatusIcon state className={classes.icon} />
          Promo Code accepted for {promoCode.summary}
        </FormHelperText>
      )}
    </div>
  );
};

export default PromotionCodeInput;
